import { useCallback, useEffect, useState } from 'react';

import { IHistoricalOrder } from '@rbi-ctg/menu';
import { ItemAvailabilityStatus } from 'enums/menu';
import { RbiOrderStatus } from 'generated/rbi-graphql';
import useIsComponentMounted from 'hooks/use-is-component-mounted';
import { useIsOrderInProgress } from 'hooks/use-is-order-in-progress';
import { useUserOrders } from 'hooks/use-user-orders';
import { useFlag } from 'state/launchdarkly';
import { useMapToFrontendCartEntry } from 'state/order/hooks/use-map-to-frontend-cart-entry';
import { useStoreContext } from 'state/store';
import { getSelectionsListFromEntry } from 'utils/cart';
import { LaunchDarklyFlag } from 'utils/launchdarkly';

import { MappedCartEntry, MappedOrder } from '../types';

const hasSufficientAvailability = (orderEntries: MappedCartEntry[]): boolean => {
  // @TODO: Check what happens if cart extra is unavailable
  const unavailableCartEntries = orderEntries.filter(
    entry =>
      entry.status !== ItemAvailabilityStatus.AVAILABLE &&
      entry.status !== ItemAvailabilityStatus.CART_EXTRA
  ).length;
  const totalItemsInCart = orderEntries.length || 0;
  return totalItemsInCart / 2 >= unavailableCartEntries;
};

export const useLastOrderAvailableToReorder = (): MappedOrder | undefined => {
  // this is needed to avoid calling mapServerEntriesToFrontend from effect if the component is unmounted
  const isMounted = useIsComponentMounted();
  const enableRecentItemsWithModifiers = useFlag(
    LaunchDarklyFlag.ENABLE_RECENT_ITEMS_WITH_MODIFIERS
  );
  const mapToFrontendEntry = useMapToFrontendCartEntry();
  const isOrderInProgress = useIsOrderInProgress();
  const { noStoreSelected, isStoreOpenAndAvailable } = useStoreContext();
  const [lastOrderCartEntries, setLastOrderCartEntries] = useState<MappedCartEntry[]>();

  const { orders } = useUserOrders({
    limit: 1,
    orderStatuses: [RbiOrderStatus.INSERT_SUCCESSFUL, RbiOrderStatus.PAYMENT_SUCCESSFUL],
  });

  const lastOrder = orders?.[0];

  const mapServerEntriesToFrontend = useCallback(
    async (lastOrderCart?: IHistoricalOrder['cart']) => {
      if (!lastOrderCart?.cartEntries?.length) {
        return null;
      }

      const orderCartEntries = await Promise.all(
        lastOrderCart.cartEntries.map(async backendCartEntry => {
          const { status, cartEntry } = await mapToFrontendEntry(backendCartEntry);
          const itemSelections =
            enableRecentItemsWithModifiers && cartEntry?.children
              ? getSelectionsListFromEntry(cartEntry)
              : [];

          return {
            ...cartEntry,
            itemSelections,
            status,
            // @TODO: Check what happens if cart extra is unavailable
            isAvailable: [
              ItemAvailabilityStatus.AVAILABLE,
              ItemAvailabilityStatus.CART_EXTRA,
            ].includes(status),
          };
        })
      );
      if (!isMounted.current) {
        return null;
      }
      return setLastOrderCartEntries(orderCartEntries);
    },
    [isMounted, mapToFrontendEntry, enableRecentItemsWithModifiers]
  );

  useEffect(() => {
    if (lastOrder) {
      mapServerEntriesToFrontend(lastOrder?.cart);
    }
  }, [mapServerEntriesToFrontend, lastOrder]);

  if (noStoreSelected || !isStoreOpenAndAvailable || isOrderInProgress || !lastOrder) {
    return undefined;
  }

  const orderIsValid =
    lastOrder && lastOrderCartEntries && hasSufficientAvailability(lastOrderCartEntries);

  return orderIsValid ? { ...lastOrder, orderEntries: lastOrderCartEntries } : undefined;
};
